/**
 * SettleNowCard — pinned at top of the cashier's Reports tab. Shows the
 * live "currently owed to operator" amount (since their last cashier_payout)
 * and a one-click button to record the settle.
 *
 * Backed by:
 *   GET  /api/cashier/owed-to-operator   — live counter
 *   POST /api/cashier/settle-now         — records the payout
 *
 * The same `cashier_payout` transaction is created on either side
 * (operator-initiated Mark Paid or cashier-initiated Settle Now), so the
 * operator's Profit tab "currently owed" reconciles to $0 either way.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { HandCoins, Loader2, Check, RefreshCw, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const SettleNowCard = () => {
    const [data, setData] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [trailOpen, setTrailOpen] = useState(false);
    const [trailLoading, setTrailLoading] = useState(false);
    const [trail, setTrail] = useState(null);

    const openTrail = async (payoutId) => {
        setTrailOpen(true);
        setTrail(null);
        setTrailLoading(true);
        try {
            const { data } = await axios.get(`${API}/cashier/payouts/${payoutId}/trail`, { withCredentials: true });
            setTrail(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not load trail');
            setTrailOpen(false);
        } finally { setTrailLoading(false); }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [owedRes, histRes] = await Promise.all([
                axios.get(`${API}/cashier/owed-to-operator`, { withCredentials: true }),
                axios.get(`${API}/cashier/my-payouts?limit=3`, { withCredentials: true }).catch(() => ({ data: { payouts: [] } })),
            ]);
            setData(owedRes.data);
            setHistory(histRes.data?.payouts || []);
        } catch { /* role gate handled by parent */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openDialog = () => {
        setAmount(String(data?.owed_to_operator || 0));
        setNote('');
        setOpen(true);
    };

    const submit = async () => {
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt <= 0) {
            toast.error('Enter the amount you paid');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/cashier/settle-now`,
                { amount: amt, note: note.trim() || null },
                { withCredentials: true });
            toast.success(`Recorded ${fmt(amt)} paid to operator`);
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not record settle');
        } finally { setBusy(false); }
    };

    if (loading && !data) return null;
    if (!data) return null;

    const owed = data.owed_to_operator || 0;
    const keep = data.your_keep || 0;
    const sharePct = Math.round((data.operator_share_pct || 0.80) * 100);

    return (
        <div className="rounded-md border border-amber-500/40 bg-gradient-to-br from-amber-950/40 to-zinc-900/40 p-5"
            data-testid="settle-now-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[11px] uppercase tracking-widest text-amber-200/80 font-semibold">
                        You currently owe operator
                    </p>
                    <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-3xl font-mono font-bold text-amber-300"
                            data-testid="settle-owed-amount">
                            {fmt(owed)}
                        </span>
                        <span className="text-xs text-zinc-500">
                            ({sharePct}% of {fmt(data.net)} net since last payout)
                        </span>
                    </div>
                    {keep > 0 && (
                        <p className="text-xs text-cyan-300 mt-1">
                            You keep <span className="font-mono font-bold">{fmt(keep)}</span>.
                        </p>
                    )}
                    {data.last_payout_at && (
                        <p className="text-[11px] text-zinc-500 mt-1">
                            Last paid: {new Date(data.last_payout_at).toLocaleString()}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={load}
                        className="border-zinc-700 bg-zinc-900 text-zinc-300"
                        data-testid="settle-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button onClick={openDialog} disabled={owed <= 0}
                        className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                        data-testid="settle-now-btn">
                        <HandCoins className="w-4 h-4 mr-1.5" /> Settle now
                    </Button>
                </div>
            </div>

            {history.length > 0 && (
                <div className="mt-4 pt-3 border-t border-amber-500/20" data-testid="settle-history-strip">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
                        Recent settles
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {history.map((h, i) => (
                            <button key={i} type="button"
                                onClick={() => openTrail(h.id)}
                                className="text-left rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2 hover:border-amber-500/40 hover:bg-zinc-900 transition-colors focus:outline-none focus:ring-1 focus:ring-amber-500/60"
                                data-testid={`settle-history-row-${i}`}>
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="font-mono text-sm text-amber-300 font-semibold">
                                        {fmt(h.amount)}
                                    </span>
                                    <span className="text-[10px] text-zinc-500 uppercase">
                                        {h.initiated_by_role === 'cashier' ? 'by you' : 'by operator'}
                                    </span>
                                </div>
                                <p className="text-[11px] text-zinc-400 mt-0.5">
                                    {new Date(h.created_at).toLocaleString(undefined, {
                                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                                    })}
                                </p>
                                {h.note && (
                                    <p className="text-[11px] text-zinc-500 mt-0.5 truncate" title={h.note}>
                                        {h.note}
                                    </p>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[#121212] border-white/10" data-testid="settle-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white">Settle with operator</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Confirm the cash amount you just handed to the operator. This zeros
                            your "currently owed" counter and starts a fresh accrual.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-white text-xs">Amount paid ($)</Label>
                            <Input type="number" min="0" step="0.01" value={amount}
                                onChange={e => setAmount(e.target.value)}
                                className="mt-1 bg-zinc-950 border-zinc-700 text-white font-mono text-lg"
                                data-testid="settle-amount-input" />
                            <p className="text-[11px] text-zinc-500 mt-1">
                                Currently owed: <span className="text-amber-300 font-mono">{fmt(owed)}</span>
                            </p>
                        </div>
                        <div>
                            <Label className="text-white text-xs">Note (optional)</Label>
                            <Input value={note}
                                onChange={e => setNote(e.target.value)}
                                placeholder="e.g. Friday weekly settle"
                                className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                                data-testid="settle-note-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}
                            className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">
                            Cancel
                        </Button>
                        <Button onClick={submit} disabled={busy}
                            className="bg-amber-500 hover:bg-amber-400 text-zinc-900"
                            data-testid="settle-confirm-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                            Confirm payout
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={trailOpen} onOpenChange={setTrailOpen}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto"
                    data-testid="trail-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-baseline gap-3">
                            Payout trail
                            {trail?.payout && (
                                <span className="text-amber-300 font-mono">{fmt(trail.payout.amount)}</span>
                            )}
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            {trail?.payout ? (
                                <>Settled {new Date(trail.payout.created_at).toLocaleString()} ·{' '}
                                {trail.payout.initiated_by_role === 'cashier' ? 'by you' : `acknowledged by ${trail.payout.operator_name || 'operator'}`}</>
                            ) : 'Loading...'}
                        </DialogDescription>
                    </DialogHeader>

                    {trailLoading && (
                        <div className="flex items-center justify-center py-6 text-zinc-500">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading trail…
                        </div>
                    )}

                    {trail && !trailLoading && (
                        <div className="space-y-3 py-2">
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="rounded border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                                    <p className="text-[10px] uppercase text-zinc-500">Loaded</p>
                                    <p className="font-mono text-emerald-300 font-semibold">{fmt(trail.totals.loaded)}</p>
                                </div>
                                <div className="rounded border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                                    <p className="text-[10px] uppercase text-zinc-500">Cashed</p>
                                    <p className="font-mono text-rose-300 font-semibold">{fmt(trail.totals.cashed)}</p>
                                </div>
                                <div className="rounded border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                                    <p className="text-[10px] uppercase text-zinc-500">Net</p>
                                    <p className="font-mono text-amber-300 font-semibold">{fmt(trail.totals.net)}</p>
                                </div>
                            </div>

                            <p className="text-[11px] text-zinc-500">
                                {trail.window?.from
                                    ? <>Window: {new Date(trail.window.from).toLocaleString()} → {new Date(trail.window.to).toLocaleString()}</>
                                    : <>Window: all activity up to {new Date(trail.window.to).toLocaleString()}</>}
                                {' · '}{trail.totals.txn_count} txns
                            </p>

                            {trail.transactions.length === 0 ? (
                                <p className="text-sm text-zinc-500 italic py-4 text-center">
                                    No customer activity in this settle window.
                                </p>
                            ) : (
                                <div className="rounded border border-zinc-800 divide-y divide-zinc-800 max-h-72 overflow-y-auto"
                                    data-testid="trail-txns">
                                    {trail.transactions.map(tx => {
                                        const isLoad = tx.type === 'cashier_load';
                                        return (
                                            <div key={tx.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                                                {isLoad
                                                    ? <ArrowDownCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                                    : <ArrowUpCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-zinc-200 truncate">
                                                        {tx.player_name || 'Unknown'}
                                                        {tx.player_phone && (
                                                            <span className="text-zinc-500 ml-1">· {tx.player_phone}</span>
                                                        )}
                                                    </p>
                                                    {tx.note && (
                                                        <p className="text-[10px] text-zinc-500 truncate">{tx.note}</p>
                                                    )}
                                                </div>
                                                <span className={`font-mono font-semibold ${isLoad ? 'text-emerald-300' : 'text-rose-300'}`}>
                                                    {isLoad ? '+' : '−'}{fmt(tx.amount)}
                                                </span>
                                                <span className="text-[10px] text-zinc-500 shrink-0 w-20 text-right">
                                                    {new Date(tx.created_at).toLocaleString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTrailOpen(false)}
                            className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"
                            data-testid="trail-close-btn">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SettleNowCard;
